import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../config/i18n';
import { Loading } from '../../components/common';
import { TeamCard } from './TeamCard';
import { useStore } from '../../store';
import { settingsApi } from '../../services/api/settings.api';
import { sessionsApi } from '../../services/api/sessions.api';
import { LanguageSelector } from '../../components/common/LanguageSelector';
import type { Language, SessionCheckErrorCode, TeamName } from '@business-game/shared';
import { applyPlayerLanguage } from '../../utils/sessionI18n';

const SESSION_ERROR_TRANSLATION_KEYS: Record<SessionCheckErrorCode, string> = {
  SESSION_NOT_FOUND: 'game.session_not_found',
  SESSION_CLOSED: 'game.session_closed',
};

const SUPPORTED_LANGUAGE_CODES: readonly string[] = ['fr', 'en', 'es', 'de', 'nl', 'pt'];

function isLanguageCode(param: string): boolean {
  return SUPPORTED_LANGUAGE_CODES.includes(param.toLowerCase());
}

function looksLikeSessionCode(param: string): boolean {
  return /^[A-Za-z0-9]{4}$/.test(param);
}

const teams: Array<{ number: number; name: TeamName }> = [
  { number: 1, name: 'team1' },
  { number: 2, name: 'team2' },
  { number: 3, name: 'team3' },
  { number: 4, name: 'team4' },
];

export const SelectPlayer: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sessionCode: sessionCodeFromPath } = useParams<{ sessionCode?: string }>();
  const [searchParams] = useSearchParams();
  const sessionCodeFromQuery = searchParams.get('session');
  const equipeParam = searchParams.get('equipe');
  const equipeNumber = equipeParam ? parseInt(equipeParam, 10) : NaN;

  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [sessionCode, setSessionCode] = useState<string>('');
  const [sessionCodeInput, setSessionCodeInput] = useState<string>('');
  const [sessionValid, setSessionValid] = useState<boolean>(false);
  const [sessionLanguage, setSessionLanguage] = useState<Language | null>(null);
  const [sessionError, setSessionError] = useState<string>('');
  const [validatingSession, setValidatingSession] = useState(false);
  const [codePasted, setCodePasted] = useState(false);
  const { teamName, currentRound, setTeam, setGameConfig, setSession } = useStore();
  const sessionLanguageLabelKey = {
    fr: 'language.french',
    en: 'language.english',
    es: 'language.spanish',
    de: 'language.german',
    nl: 'language.dutch',
    pt: 'language.portuguese',
  } as const;
  const titleText = t('selectPlayer.title');
  const titleAccentText = t('selectPlayer.titleAccent');
  const shouldAppendTitleAccent =
    Boolean(titleAccentText) &&
    titleAccentText !== 'selectPlayer.titleAccent' &&
    !titleText.toLowerCase().includes(titleAccentText.toLowerCase());

  // Parse path param: can be language (fr, en, es...) or session code (4 alphanumeric chars)
  const pathParam = sessionCodeFromPath?.toLowerCase() ?? null;
  const isPathLanguage = pathParam && isLanguageCode(pathParam);
  const isPathSessionCode = sessionCodeFromPath && looksLikeSessionCode(sessionCodeFromPath) && !isPathLanguage;

  // Session code from URL: only if path param looks like a session code (4 chars), not a language
  const sessionCodeFromUrl = isPathSessionCode
    ? sessionCodeFromPath
    : sessionCodeFromQuery || null;

  // Apply language from URL path (e.g. /select-player/en)
  useEffect(() => {
    if (isPathLanguage && pathParam) {
      i18n.changeLanguage(pathParam);
    }
  }, [isPathLanguage, pathParam]);

  // Check session code from URL or localStorage
  useEffect(() => {
    const checkSession = async () => {
      // Priority: URL param > localStorage
      const codeFromStorage = !sessionCodeFromUrl ? localStorage.getItem('session_code') : null;
      const codeToCheck = sessionCodeFromUrl || codeFromStorage;

      if (codeToCheck) {
        setValidatingSession(true);
        try {
          const result = await sessionsApi.checkSession(codeToCheck.toUpperCase());
          if (result.valid && result.session) {
            await applyPlayerLanguage(result.session.language, result.session.gameMode);
            setSessionCode(codeToCheck.toUpperCase());
            setSessionLanguage((result.session.language as Language | null) ?? null);
            setSession(codeToCheck.toUpperCase());
            setSessionValid(true);
            setSessionError('');
            // Update URL if code was from localStorage
            if (!sessionCodeFromUrl) {
              navigate(`/select-player/${codeToCheck.toUpperCase()}`, { replace: true });
            }
          } else {
            setSessionValid(false);
            // If code came from localStorage (user didn't enter it), clear it and don't show error
            if (codeFromStorage) {
              localStorage.removeItem('session_code');
              setSessionError('');
            } else {
              // Code from URL: show translated error based on the stable code
              const translatedMessage = result.code
                ? t(SESSION_ERROR_TRANSLATION_KEYS[result.code])
                : result.message || t('game.session_invalid');
              setSessionError(translatedMessage);
              navigate('/select-player', { replace: true });
            }
          }
        } catch (error) {
          console.error('Failed to check session:', error);
          setSessionValid(false);
          if (codeFromStorage) {
            localStorage.removeItem('session_code');
            setSessionError('');
          } else {
            setSessionError(t('common.error'));
          }
        } finally {
          setValidatingSession(false);
          setIsInitializing(false);
        }
      } else {
        setIsInitializing(false);
      }
    };

    checkSession();
  }, [sessionCodeFromUrl, setSession, navigate, t]);

  useEffect(() => {
    if (!sessionValid || !sessionCode || validatingSession || isInitializing) {
      return;
    }
    if (!Number.isInteger(equipeNumber) || equipeNumber < 1 || equipeNumber > 4) {
      return;
    }

    const teamNameForEquipe = `team${equipeNumber}` as TeamName;
    setTeam(teamNameForEquipe, equipeNumber);
    navigate(`/game/${sessionCode}`, { replace: true });
  }, [sessionValid, sessionCode, equipeNumber, validatingSession, isInitializing, setTeam, navigate]);

  // If team already selected and round exists, redirect to game
  useEffect(() => {
    if (Number.isInteger(equipeNumber) && equipeNumber >= 1 && equipeNumber <= 4) {
      return;
    }
    if (teamName && currentRound && sessionValid) {
      navigate(sessionCode ? `/game/${sessionCode}` : '/game', { replace: true });
    }
  }, [teamName, currentRound, sessionValid, sessionCode, navigate, equipeNumber]);

  // Load game config on mount
  useEffect(() => {
    const loadGameConfig = async () => {
      try {
        const config = await settingsApi.getGameConfig();
        setGameConfig(config);
      } catch (error) {
        console.error('Failed to load game config:', error);
        // Use defaults if API fails
        setGameConfig({
          baseScore: 20,
          maxPointsPerRound: 3,
          numberOfRounds: 10,
        });
      }
    };

    loadGameConfig();
  }, [setGameConfig]);

  const handleSessionCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = sessionCodeInput.trim().toUpperCase();

    if (code.length !== 4) {
      setSessionError(t('selectPlayer.sessionCodeLength'));
      return;
    }

    setValidatingSession(true);
    setSessionError('');

    try {
      const result = await sessionsApi.checkSession(code);
      if (result.valid && result.session) {
        await applyPlayerLanguage(result.session.language, result.session.gameMode);
        setSessionCode(code);
        setSessionLanguage((result.session.language as Language | null) ?? null);
        setSession(code);
        setSessionValid(true);
        setSessionError('');
        navigate(`/select-player/${code}`, { replace: true });
      } else {
        setSessionValid(false);
        setSessionError(
          result.code
            ? t(SESSION_ERROR_TRANSLATION_KEYS[result.code])
            : result.message || t('game.session_invalid')
        );
      }
    } catch (error) {
      console.error('Failed to check session:', error);
      setSessionError(t('common.error'));
    } finally {
      setValidatingSession(false);
    }
  };

  const handleTeamSelect = (teamName: TeamName, teamNumber: number) => {
    if (!sessionValid) {
      setSessionError(t('selectPlayer.selectSessionFirst'));
      return;
    }

    setIsLoading(true);

    // Save team selection
    setTeam(teamName, teamNumber);

    // Navigate to game
    navigate(sessionCode ? `/game/${sessionCode}` : '/game');
  };

  if (isInitializing) {
    return <Loading fullScreen message={t('selectPlayer.loading')} />;
  }

  return (
    <div className="min-h-screen flex flex-col relative overflow-x-hidden font-sans text-slate-900 bg-slate-50">
      {/* Background Layer */}
      <div className="fixed inset-0 -z-50 h-full w-full bg-slate-50 overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        <div className="absolute left-1/2 top-0 -translate-x-1/2 -z-10 h-[800px] w-[800px] rounded-full bg-indigo-500 opacity-20 blur-[120px] pointer-events-none"></div>
        <div className="absolute right-0 bottom-0 z-0 h-[600px] w-[600px] rounded-full bg-fuchsia-500 opacity-10 blur-[150px] pointer-events-none"></div>
      </div>

      {/* Top Configuration Bar */}
      <div className="w-full bg-white/60 backdrop-blur-xl border-b border-white/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Brand */}
            <div className="flex items-center gap-4 group">
              <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center shadow-lg shadow-slate-300 transform group-hover:scale-105 transition-transform duration-300">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.384-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
              </div>
              <span className="font-bold text-slate-900 tracking-tight text-xl">Team Lab / Lead Lab</span>
            </div>

            {sessionValid && sessionCode ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700">
                {t('language.label')}: {t(sessionLanguageLabelKey[(sessionLanguage || 'fr') as keyof typeof sessionLanguageLabelKey])}
              </div>
            ) : (
              <LanguageSelector />
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-grow flex flex-col items-center justify-center p-4 sm:p-8 relative z-10">
        <div className="max-w-[1400px] w-full space-y-16">
          {/* Session Code Input (if not valid) */}
          {!sessionValid && !validatingSession && (
            <div className="max-w-md mx-auto w-full slide-up">
              <div className="bg-white rounded-3xl shadow-xl border border-slate-200 p-8">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-2">{t('selectPlayer.sessionCodeTitle')}</h2>
                  <p className="text-slate-500 text-sm">{t('selectPlayer.sessionCodeSubtitle')}</p>
                </div>

                <form onSubmit={handleSessionCodeSubmit} className="space-y-4">
                  <div className="relative">
                    <input
                      type="text"
                      value={sessionCodeInput}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4);
                        setSessionCodeInput(value);
                        setSessionError('');

                        // Detect paste: if value length jumps to 4, it was likely pasted
                        if (value.length === 4 && sessionCodeInput.length < 4) {
                          setCodePasted(true);
                          setTimeout(() => setCodePasted(false), 600);
                        }
                      }}
                      onPaste={(e) => {
                        // Handle paste event
                        setTimeout(() => {
                          const pastedValue = e.clipboardData.getData('text').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4);
                          if (pastedValue.length === 4) {
                            setCodePasted(true);
                            setTimeout(() => setCodePasted(false), 600);
                          }
                        }, 0);
                      }}
                      placeholder="ABCD"
                      maxLength={4}
                      className={`w-full px-6 py-4 bg-slate-50 border-2 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 font-mono font-black text-slate-900 text-2xl text-center tracking-wider transition-all ${
                        codePasted
                          ? 'border-emerald-500 bg-emerald-50 scale-105 shadow-lg shadow-emerald-200'
                          : 'border-slate-200'
                      }`}
                      autoFocus
                    />
                    {codePasted && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="bg-emerald-500 text-white rounded-full px-4 py-2 text-xs font-bold animate-pulse">
                          ✓ {t('selectPlayer.codePasted')}
                        </div>
                      </div>
                    )}
                  </div>

                  {sessionError && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                      <p className="text-sm text-red-700 font-medium text-center">{sessionError}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={sessionCodeInput.length !== 4 || validatingSession}
                    className="w-full px-6 py-4 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-2xl font-black text-lg shadow-lg shadow-indigo-200 hover:shadow-indigo-300 transition-all"
                  >
                    {validatingSession ? t('selectPlayer.validatingCode') : t('selectPlayer.validateCode')}
                  </button>
                </form>
              </div>
            </div>
          )}

          {/* Validating Session */}
          {validatingSession && (
            <div className="max-w-md mx-auto w-full">
              <div className="bg-white rounded-3xl shadow-xl border border-slate-200 p-8 text-center">
                <div className="relative w-16 h-16 mx-auto mb-6">
                  <div className="absolute inset-0 border-4 border-slate-100 rounded-full"></div>
                  <div className="absolute inset-0 border-4 border-indigo-600 rounded-full border-t-transparent animate-spin"></div>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('selectPlayer.validatingCode')}</h3>
                <p className="text-slate-500 text-sm">{t('selectPlayer.pleaseWait')}</p>
              </div>
            </div>
          )}

          {/* Header Section */}
          {sessionValid && (
            <>
              <div className="text-center space-y-6 max-w-4xl mx-auto slide-up" style={{ animationDelay: '0.1s' }}>
                <h1 className="text-5xl sm:text-7xl font-black text-slate-900 tracking-tighter leading-tight drop-shadow-sm">
                  {titleText}
                  {shouldAppendTitleAccent ? ' ' : ''}
                  {shouldAppendTitleAccent ? (
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-violet-600">{titleAccentText}</span>
                  ) : null}
                </h1>
                <p className="text-xl sm:text-2xl text-slate-500 font-medium max-w-2xl mx-auto">
                  {t('selectPlayer.subtitle')}
                </p>
                {sessionCode && (
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 border border-indigo-200 rounded-xl">
                    <span className="text-xs font-bold text-indigo-600 uppercase tracking-wider">{t('selectPlayer.sessionLabel')}</span>
                    <span className="font-mono font-black text-indigo-700 text-lg">{sessionCode}</span>
                  </div>
                )}
              </div>

              {/* Teams Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 px-4 sm:px-0">
                {teams.map((team) => (
                  <TeamCard
                    key={team.name}
                    teamNumber={team.number}
                    onSelect={() => handleTeamSelect(team.name, team.number)}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Loading Overlay */}
        {isLoading && (
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center transition-opacity duration-300">
            <div className="bg-white rounded-xl p-8 shadow-2xl text-center max-w-sm mx-4 w-full">
              <div className="relative w-16 h-16 mx-auto mb-6">
                <div className="absolute inset-0 border-4 border-slate-100 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-indigo-600 rounded-full border-t-transparent animate-spin"></div>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('selectPlayer.initializing')}</h3>
              <p className="text-slate-500 text-sm">{t('selectPlayer.loading')}</p>
            </div>
          </div>
        )}
      </main>

      {/* Footer Note - in normal document flow, after the team grid */}
      <footer className="w-full bg-slate-100 border-t border-slate-200 py-6 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center space-y-2">
            <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wider">{t('selectPlayer.aboutTitle')}</h4>
            <p className="text-xs md:text-sm text-slate-600 leading-relaxed max-w-2xl mx-auto">
              {t('selectPlayer.aboutText')}
            </p>
          </div>
          <Link
            to="/admin/login"
            className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 text-[10px] text-slate-400/80 hover:text-slate-500 transition-colors"
          >
            Admin
          </Link>
        </div>
      </footer>
    </div>
  );
};
